import {
  Calculate as CalculateIcon,
  CancelRounded as CancelRoundedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from '@mui/icons-material';
import {
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { ExtrapolationColors } from './utils/extrapolation-colors';

export function ExtrapolationInput({
  value,
  onChange,
  personCount,
  showWarning,
}: {
  value: string;
  onChange: (v: string) => void;
  personCount: number;
  showWarning: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    onChange('');
    setIsOpen(false);
  };

  if (isOpen === false) {
    return <CloseCaseButton handleOpen={handleOpen} />;
  } else {
    return (
      <OpenCaseInput
        value={value}
        onChange={onChange}
        personCount={personCount}
        showWarning={showWarning}
        handleClose={handleClose}
      />
    );
  }
}

function CloseCaseButton({ handleOpen }: { handleOpen: () => void }) {
  return (
    <FSPrimaryButton
      color="info"
      variant="outlined"
      startIcon={<CalculateIcon />}
      onClick={handleOpen}
    >
      {translate('Surveys.extrapolate-action.button-title')}
    </FSPrimaryButton>
  );
}

function OpenCaseInput({
  value,
  onChange,
  personCount,
  showWarning,
  handleClose,
}: {
  value: string;
  onChange: (v: string) => void;
  personCount: number;
  showWarning: boolean;
  handleClose: () => void;
}) {
  const isSet = value !== '';
  return (
    <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
      {showWarning && (
        <Tooltip
          title={translate('Surveys.extrapolate-action.warning-big-factor')}
          arrow
          placement="top"
          componentsProps={{ tooltip: { sx: { textAlign: 'center' } } }}
        >
          <WarningAmberRoundedIcon
            sx={{ color: ExtrapolationColors.warning }}
          />
        </Tooltip>
      )}
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          border: `1px solid ${ColorsRaw.anthracite.Anthracite_5}`,
          borderRadius: '12px',
          pl: 2,
          pr: 1,
          height: 40,
        }}
      >
        <Typography
          color={ColorsRaw.secondary.Light_Grey}
          sx={{ fontSize: '1rem' }}
        >
          {translate('Surveys.extrapolate-action.pass-from')}
        </Typography>
        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
          {personCount}
        </Typography>
        <Typography
          color={ColorsRaw.secondary.Light_Grey}
          sx={{ fontSize: '1rem' }}
        >
          {translate('Surveys.extrapolate-action.pass-to')}
        </Typography>
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
          autoFocus
          placeholder="—"
          inputProps={{
            inputMode: 'numeric',
            sx: { padding: 0, width: 50, textAlign: 'center' },
          }}
          sx={{
            padding: 0,
            '& .MuiInputBase-root': {
              fontWeight: isSet ? 'bold' : 'normal',
            },
            '& .MuiInputBase-input': {
              color: ExtrapolationColors.overrideValue,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        />
        <Typography
          color={ColorsRaw.secondary.Light_Grey}
          sx={{ fontSize: '1rem' }}
        >
          {translate('Surveys.extrapolate-action.persons')}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: ColorsRaw.basic.Cream }}>
          <CancelRoundedIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
}
